import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Joyride, { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useDispatch, useSelector } from "react-redux";
import { open } from "../../Redux/collapseSlice";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";

const TOUR_VERSION = "app-tour-v4";
const TOUR_STATUS_DONE = "done";

// Each entry produces a pair of Joyride steps:
//   1. Nav step  — spotlights the sidebar item, orients the user.
//   2. Page step — navigates to the page, spotlights the page content,
//                  shows the full description.
const BASE_STEP_PAIRS = [
  {
    navTarget: '[data-tour="sidenav-dashboard"]',
    pageTarget: '[data-tour="page-dashboard"]',
    name: "Dashboard",
    permissionKey: "dashboard",
    navContent: "This is your clinical overview. Use this tab to monitor patient activity, track implant progress, and view key metrics—all in one place.",
    pageContent: "Use the dashboard to track procedural outcomes, understand patient demographics, and monitor referral trends—all in one place.",
    route: "/",
    pageDisableScrolling: true,
  },
  {
    navTarget: '[data-tour="sidenav-patients"]',
    pageTarget: '[data-tour="page-patients"]',
    name: "Patients",
    permissionKey: "patients",
    navContent: "Centralized management for your entire patient registry.",
    pageContent: "View comprehensive patient information, including pre-implant status, procedures, tasks, and communication history.",
    route: "/patients",
  },
  {
    navTarget: '[data-tour="sidenav-tasks"]',
    pageTarget: '[data-tour="page-tasks"]',
    name: "Tasks",
    permissionKey: "tasks",
    navContent: "Keep everything on track. Manage all pending tasks across your patients—from initial details to follow-ups.",
    pageContent: "Manage your workflow by sorting tasks by due date or status. Pin urgent items and use filters to quickly find what you need.",
    route: "/tasks",
  },
  {
    navTarget: '[data-tour="sidenav-case-days"]',
    pageTarget: '[data-tour="page-case-days"]',
    name: "Procedure Days",
    permissionKey: "case-days",
    navContent: "Manage your clinical calendar with ease. This section allows you to organize and view all scheduled procedures.",
    pageContent: "Stay on top of your schedule. Track and manage all implant procedures—from upcoming to completed.",
    route: "/case-days",
  },
  {
    navTarget: '[data-tour="sidenav-registry"]',
    pageTarget: '[data-tour="page-registry"]',
    name: "Registry",
    permissionKey: "registry",
    navContent: "Track NCDR registry submissions for each patient after implantation.",
    pageContent: "Monitor quarterly progress, find incomplete submissions, and review completed files to stay compliant.",
    route: "/registry",
  },
  {
    navTarget: '[data-tour="sidenav-contacts"]',
    pageTarget: '[data-tour="page-contacts"]',
    name: "Contacts",
    permissionKey: "contacts",
    navContent: "Stay connected. Manage and access contact details for system users and external providers in one place.",
    pageContent: "Quickly find team members by role or affiliation and coordinate with physicians, admins, or external providers.",
    route: "/contacts",
  },
  {
    navTarget: '[data-tour="sidenav-messages"]',
    pageTarget: '[data-tour="messages-page"]',
    name: "Messages",
    permissionKey: "communicationMessages",
    navContent: "Streamline patient communication. This portal automates outreach, managing appointment reminders and follow-ups.",
    pageContent: "Monitor patient responses using confirmations, declines, and reschedule indicators to keep follow-ups on track.",
    route: "/messages",
  },
];

const MANAGEMENT_STEP_PAIR = {
  // Target just the Management tab button so the tooltip appears right next to
  // it and the sidebar scrollbar remains usable.
  navTarget: '[data-tour="sidenav-management"]',
  // No pageTarget/pageContent: we only want a single Management nav step.
  name: "Management",
  navContent: `Management — Super Admins Only

Use this hub to manage your setup. Configure users & roles, centers, devices, medications, insurances, indications, complications, and message templates.`,
  route: "/users",
};

/** Build the flat Joyride steps array from the step-pair definitions. */
function buildSteps(pairs) {
  const total = pairs.length;
  return pairs.flatMap((pair, i) => {
    const steps = [
      // Nav step — just a pointer, no section number in title.
      {
        target: pair.navTarget,
        title: pair.name,
        content: pair.navContent,
        route: pair.route,
        placement: "right",
        disableBeacon: true,
        // Keep the sidebar scrollable during nav steps by removing the dark
        // overlay. The tooltip still appears next to the nav item, but the
        // user can scroll the sidebar freely.
        disableOverlay: true,
      },
    ];

    // Optional page step — only when pageTarget/pageContent are provided.
    if (pair.pageTarget && pair.pageContent) {
      steps.push({
        target: pair.pageTarget,
        title: `${pair.name}`,
        content: pair.pageContent,
        route: pair.route,
        placement: pair.pagePlacement ?? "bottom",
        disableBeacon: true,
        ...(pair.pageDisableScrolling && { disableScrolling: true }),
      });
    }

    return steps;
  });
}

const AppTour = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s.authenticationValues.currentUser);
  const userId = currentUser?.id;
  const permissions = useSelector(
    (s) => s.authenticationValues.currentUser?.permissions ?? {},
  );
  const navigate = useNavigate();

  // Mirror the exact access check sideList.jsx uses for the Management nav item.
  // Only Super Admin role grants access to the Management section.
  const roles = useSelector(
    (s) => s.authenticationValues.currentUser?.roles ?? [],
  );
  const hasManagementAccess = useMemo(
    () => roles.some((r) => r?.name === "Super Admin"),
    [roles],
  );

  // Build the base step-pairs list according to the same permissions logic
  // that controls which sidebar items are visible (sideList.jsx). This keeps
  // the tour aligned with the actual nav for custom roles.
  const permittedStepPairs = useMemo(() => {
    const permissionCheck = {
      dashboard: permissions?.analytics?.read,
      patients: permissions?.patients?.read,
      tasks: permissions?.tasks?.read,
      reports: permissions?.reports?.read,
      "case-days": permissions?.cases?.read,
      registry: permissions?.registry?.read,
      contacts: permissions?.contacts?.read,
      communicationMessages:
        permissions?.communication_modes?.read ||
        permissions?.communicationModes?.read,
    };

    return BASE_STEP_PAIRS.filter((pair) => {
      const key = pair.permissionKey;
      if (!key) return true;
      const allowed = permissionCheck[key];
      return allowed === undefined ? true : !!allowed;
    });
  }, [permissions]);

  // Build the step list once — frozen into a ref the moment the tour starts
  // so a late API response can't silently add/remove steps mid-tour.
  const frozenStepsRef = useRef(null);
  const tourSteps = useMemo(
    () => buildSteps(hasManagementAccess
      ? [...permittedStepPairs, MANAGEMENT_STEP_PAIR]
      : permittedStepPairs),
    [hasManagementAccess, permittedStepPairs],
  );

  const storageKey = useMemo(
    () => (userId ? `fixafib:tour:${TOUR_VERSION}:${userId}` : null),
    [userId],
  );
  const sessionKey = useMemo(
    () => (userId ? `fixafib:tour:session:${TOUR_VERSION}:${userId}` : null),
    [userId],
  );

  const [showModal, setShowModal] = useState(false);
  const [run, setRun] = useState(false);

  // Show welcome modal once per user (skipped if already done).
  useEffect(() => {
    if (!storageKey || !sessionKey || !userId) return;
    if (localStorage.getItem(storageKey) === TOUR_STATUS_DONE) return;
    if (sessionStorage.getItem(sessionKey) === TOUR_STATUS_DONE) return;

    const t = setTimeout(() => setShowModal(true), 1200);
    return () => clearTimeout(t);
  }, [storageKey, sessionKey, userId]);

  // Defensive: while the tour is running, make sure Joyride doesn't leave the
  // document/body in a scroll-locked state, so the sidebar (and page) remain
  // scrollable even mid-tour.
  useEffect(() => {
    if (!run || typeof document === "undefined") return;
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }, [run]);

  const markDone = useCallback(() => {
    setRun(false);
    frozenStepsRef.current = null;

    // Ensure any scroll locking applied while the tour was running is
    // completely reset so the layout (including the sidebar) remains
    // scrollable without needing a hard reload.
    if (typeof document !== "undefined") {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    }

    if (storageKey) localStorage.setItem(storageKey, TOUR_STATUS_DONE);
    if (sessionKey) sessionStorage.setItem(sessionKey, TOUR_STATUS_DONE);
  }, [storageKey, sessionKey]);

  const handleStartTour = () => {
    // Snapshot the step list at click-time so user-data changes mid-tour
    // (e.g. a background /me refresh) can't alter the step count or add the
    // Management steps for a user who didn't have access when they started.
    frozenStepsRef.current = tourSteps;
    setShowModal(false);
    navigate("/");
    setTimeout(() => setRun(true), 350);
  };

  const handleSkipModal = () => {
    setShowModal(false);
    markDone();
  };

  const handleJoyrideCallback = useCallback(
    ({ type, action, step, status }) => {
      // Defensive: on every Joyride event, clear any scroll locking that the
      // library may have applied to the document so the sidebar (and page)
      // remain scrollable without requiring a hard reload.
      if (typeof document !== "undefined") {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      }

      if (type === EVENTS.STEP_BEFORE) {
        if (step?.route) navigate(step.route);
        if (step?.title === "Management") dispatch(open({ management: true }));
      }
      if (action === ACTIONS.CLOSE) {
        markDone();
      }
      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        markDone();
      }
    },
    [navigate, markDone, dispatch],
  );

  if (!userId) return null;

  return (
    <>
      {/* ── Welcome modal ── */}
      <Dialog
        open={showModal}
        onClose={handleSkipModal}
        PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 420 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
          <MapOutlinedIcon color="primary" fontSize="large" />
          <Typography variant="h6" fontWeight={700}>
            Welcome to FixAFib Connect
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Would you like a quick tour of the main sections? It only takes a
            minute and will help you find your way around.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
            <Button variant="outlined" color="inherit" onClick={handleSkipModal}>
              Skip for now
            </Button>
            <Button variant="contained" onClick={handleStartTour}>
              Start Tour
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Joyride tour ── */}
      <Joyride
        steps={frozenStepsRef.current ?? tourSteps}
        run={run}
        continuous
        showSkipButton
        disableOverlayClose
        disableScrollParentFix
        styles={{
          options: {
            zIndex: 16000,
            primaryColor: theme.palette.primary.main,
          },
          buttonBack: {
            color: theme.palette.primary.main,
          },
          tooltip: {
            animation: "tourTooltipIn 0.25s ease forwards",
          },
          spotlight: {
            transition: "all 0.35s ease",
          },
          overlay: {
            transition: "opacity 0.35s ease",
          },
        }}
        locale={{
          back: "Back",
          close: "Close",
          last: "Finish",
          next: "Next",
          skip: "Skip tour",
        }}
        callback={handleJoyrideCallback}
      />
    </>
  );
};

export default AppTour;
